import React, { useEffect, useState } from "react"
import { TrashIcon } from "@heroicons/react/24/solid"
import * as DateFns from "date-fns"
import { useNavigate } from "react-router-dom"

import {
  Idea,
  FleetingIdea,
  WebSource,
  ID,
} from "../../../../../../model/types"
import { useNana } from "../../../../../hooks/useNana"
import * as Commands from "../../../../../../model/commands"
import IdeaBox, { FleetingIdeaBox } from "../../../../../components/idea_box"
import { MosaicPageState } from "../../../mosaic"
import { plainTextFromHTML } from "../../../../../../services/text"
import ShowEditor from "../../../mosaic_page/idea_pane/show_editor"
import Hub from "../../../../../../hub"
import { HelpBubble, HelpInline } from "../../../../../components/help_bubble"
import { generatePublicId } from "../../../../../../model/public_id"

const IdeasList = ({
  fleetingIdeas,
  ideas,
  deleteFleetingIdea,
  clickedOnIdea,
}: {
  fleetingIdeas: FleetingIdea[]
  ideas: Idea[]
  deleteFleetingIdea: (idea: FleetingIdea) => void
  clickedOnIdea: (idea: Idea) => void
}) => {
  return (
    <div className="flex-grow p-2 w-full flex flex-col items-start gap-1 overflow-y-scroll">
      {fleetingIdeas.length === 0 && ideas.length === 0 && (
        <div className="text-gray-400 text-sm">No ideas captured yet.</div>
      )}
      {fleetingIdeas.map((idea, i) => (
        <div
          key={"fleeting-" + idea.id}
          className="flex w-full flex-row items-start justify-between gap-2 p-1 hover:bg-sky-50 dark:hover:bg-sky-900"
        >
          <div className="flex flex-row items-start gap-2">
            <FleetingIdeaBox />
            <div
              className="text-sm font-medium"
              style={{ wordBreak: "break-word" }}
            >
              {plainTextFromHTML(idea.content)}
            </div>
          </div>
          <div
            onClick={() => deleteFleetingIdea(idea)}
            className="cursor-pointer text-sm text-gray-300 hover:bg-sky-100 dark:hover:bg-sky-700 p-2 rounded"
          >
            <TrashIcon className="h-4 w-4 text-sm" />
          </div>
        </div>
      ))}
      {ideas.map((idea, i) => (
        <div
          key={"placed-" + idea.id}
          className="flex w-full flex-row items-center justify-between p-1 hover:bg-zinc-50 dark:hover:bg-zinc-800"
          onClick={() => clickedOnIdea(idea)}
        >
          <div className="min-w-0 flex flex-row items-center gap-2 text-sm">
            <IdeaBox idea={idea} />
            <div className="min-w-0 text-ellipsis overflow-x-hidden whitespace-nowrap">
              {idea.title ? (
                idea.title
              ) : (
                <span className="italic">
                  {plainTextFromHTML(idea.content)}
                </span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

export const SourceIdeaCaptureState: {
  unsavedContentForSource: Map<ID, string>
} = {
  unsavedContentForSource: new Map(),
}

export default function SourceIdeaCapture({
  source,
  allowCaptureOfReadSource,
}: {
  source: WebSource
  allowCaptureOfReadSource: boolean
}) {
  const { model, commander, hub } = useNana()
  const navigate = useNavigate()

  const [fleetingIdeas, setFleetingIdeas] = useState<FleetingIdea[]>([])
  const [ideas, setIdeas] = useState<Idea[]>([])
  const [tick, setTick] = React.useState(0)

  const updateIdeaLists = () => {
    setFleetingIdeas(
      (model
        ? source
          ? model.getFleetingIdeasFromSource(source.id)
          : model.getFleetingIdeasWithNoSource()
        : []
      ).sort((a, b) => DateFns.compareDesc(a.createdAt, b.createdAt))
    )
    setIdeas(model.getIdeasFromSource(source.id))
  }

  useEffect(() => {
    updateIdeaLists()
    const keys = [
      hub.on("model:fleeting-idea-created", updateIdeaLists),
      hub.on("model:fleeting-idea-deleted", updateIdeaLists),
    ]
    return () => {
      keys.forEach((k) => hub.remove(k))
    }
  }, [source])

  const createFleetingIdea = () => {
    if (
      SourceIdeaCaptureState.unsavedContentForSource.get(source.id) ==
        "<p></p>" ||
      SourceIdeaCaptureState.unsavedContentForSource.get(source.id) == undefined
    )
      return
    const id = generatePublicId()
    commander.exec(
      new Commands.CreateFleetingIdeaCommand({
        id,
        fleetingIdea: {
          id,
          content:
            SourceIdeaCaptureState.unsavedContentForSource.get(source.id) ||
            "<p></p>",
          sourceId: source.id,
          createdAt: new Date(),
        },
      })
    )
    SourceIdeaCaptureState.unsavedContentForSource.delete(source.id)
    setTick((tick) => tick + 1)
  }

  const deleteFleetingIdea = (idea: FleetingIdea) => {
    commander.exec(new Commands.DeleteFleetingIdeaCommand({ id: idea.id }))
  }

  const clickedOnIdea = (idea: Idea) => {
    MosaicPageState.forceOpenLocation = idea.location
    navigate("/app")
  }

  if (
    !source ||
    source.status == "new" ||
    source.status == "queued" ||
    allowCaptureOfReadSource
  ) {
    return (
      <>
        <div className="flex h-full min-h-0 w-full grow flex-col justify-stretch overflow-hidden border-b border-gray-100 bg-white dark:border-gray-800 dark:bg-gray-800">
          <div className="min-h-0 flex flex-col p-4 bg-gray-50 dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800">
            <form
              className="min-h-0 max-h-44 md:max-h-80 flex flex-col gap-4"
              onSubmit={(e) => {
                e.preventDefault()
                createFleetingIdea()
              }}
            >
              <div className="min-h-0 xmax-h-44 xmd:max-h-80 flex flex-col shadow">
                <ShowEditor
                  objectKey={tick}
                  initialContent={
                    SourceIdeaCaptureState.unsavedContentForSource.get(
                      source.id
                    ) || "<p></p>"
                  }
                  initialScrollTop={0}
                  placeholderText="Capture note to place in the mosaic later..."
                  onPageLinkClick={(idea: Idea) => {
                    MosaicPageState.forceOpenLocation = idea.location
                    navigate("/app")
                  }}
                  onUpdate={(newContent, _newSelectionPos) => {
                    SourceIdeaCaptureState.unsavedContentForSource.set(
                      source.id,
                      newContent
                    )
                  }}
                  onScroll={(scrollTop: number) => {}}
                  recreateWhen={(hub: Hub, recreate): string[] => {
                    return []
                  }}
                />
              </div>
              <div className="flex-shrink-0">
                <button
                  type="submit"
                  className="inline-flex items-center rounded-md bg-indigo-600  px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:bg-gray-500"
                >
                  Capture
                </button>
              </div>
            </form>
          </div>
          <div className="p-4 flex flex-col min-h-0 ">
            <IdeasList
              {...{
                fleetingIdeas,
                source,
                ideas,
                deleteFleetingIdea,
                clickedOnIdea,
              }}
            />
          </div>
        </div>
      </>
    )
  }

  return (
    <div className="flex h-full min-h-0 grow flex-col overflow-hidden md:p-4">
      <IdeasList
        {...{
          fleetingIdeas,
          source,
          ideas,
          deleteFleetingIdea,
          clickedOnIdea,
        }}
      />
    </div>
  )
}

export const MosaicNotesHelp = () => {
  return (
    <div className="">
      <p>
        Here, capture quick short text notes representing ideas from this
        source.
      </p>
      <p>
        Don't keep <em>every</em> idea from the source. Look for ideas that are:
      </p>
      <ul>
        <li>New to you.</li>
      </ul>
      <ul>
        <li>Relevant to your goals.</li>
      </ul>
      <ul>
        <li>Interesting and high-energy to you.</li>
      </ul>
      <p>
        Once created here, they can be placed into the Mosaic and written up
        fully as permanent notes.
      </p>
    </div>
  )
}
